import type { PdfSearchCoverageBadgeState } from './pdf-search-coverage-badge';

export interface PdfCoverageResponse {
  textIndexed?: boolean;
  textIndexingAllowed?: boolean;
  textIndexingBlockedReason?: string | null;
  status?: string;
  candidatePageCount?: number;
  processedPageCount?: number;
  skippedPageCount?: number;
  failedPageCount?: number;
  /** Scanned pages no OCR round read yet. */
  pendingPageCount?: number;
  managedCreditsAvailable?: boolean;
  pageLimit?: number;
  indexedPages?: number | null;
  /** Largest PDF whose image text the server reads, in MB. */
  imageTextFileLimitMb?: number;
  /** AI search by meaning has the PDF (its embeddings exist). */
  aiIndexed?: boolean;
}

export interface PdfPageLimitNotice {
  indexed: number;
  total: number;
  limit: number;
}

/** Names the cut when the last indexing read fewer pages than the PDF has. */
export function pdfPageLimitNotice(
  state: Pick<PdfSearchCoverageBadgeState, 'indexedPages' | 'pageLimit'>,
  pageCount: number
): PdfPageLimitNotice | null {
  const indexed = state.indexedPages;
  const limit = state.pageLimit;
  if (
    indexed === undefined ||
    indexed === null ||
    limit === undefined ||
    indexed >= pageCount
  ) {
    return null;
  }
  return { indexed, total: pageCount, limit };
}

/** Scanned pages read so far, while OCR rounds still run. */
export function imageTextProgress(
  coverage: PdfCoverageResponse
): PdfSearchCoverageBadgeState['imageTextProgress'] {
  const pending = coverage.pendingPageCount ?? 0;
  const total = coverage.candidatePageCount ?? 0;
  if (coverage.status !== 'pending' || pending === 0 || total === 0)
    return undefined;
  // Failed pages were tried, not read.
  return {
    read: total - pending - (coverage.failedPageCount ?? 0),
    total,
  };
}

export function mapCoverageStatus(
  coverage: PdfCoverageResponse
): PdfSearchCoverageBadgeState['imageTextStatus'] {
  if (coverage.status === 'pending') return 'pending';
  if (coverage.status === 'complete') return 'complete';
  if (coverage.status === 'failed') return 'failed';
  if (coverage.status === 'partial') return 'partial';
  if (coverage.status === 'skipped') return 'tooLarge';
  if ((coverage.candidatePageCount ?? 0) > 0) return 'manual';
  return 'complete';
}

export function shouldOpenPdfSearchCoverageCoachmark(
  state: PdfSearchCoverageBadgeState,
  candidatePageCount: number
): boolean {
  return (
    state.availability === 'available' &&
    state.textIndexed &&
    candidatePageCount > 0 &&
    (state.imageTextStatus === 'manual' ||
      state.imageTextStatus === 'partial' ||
      state.imageTextStatus === 'failed')
  );
}

export function requiresPdfOcrConfirmation(
  candidatePageCount: number,
  thresholdPages: number
): boolean {
  return candidatePageCount > thresholdPages;
}
